// /apps/patient/src/pages/calendar/WorkoutActionModal.tsx
import { X, PlayCircle, RefreshCw, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface WorkoutActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartWorkout: () => void;
  onSwapDay: () => void;
  workoutName: string;
  date: Date;
  subtitle?: string;
  canSwap: boolean;
  isSwapped?: boolean;
}

export function WorkoutActionModal({
  isOpen,
  onClose,
  onStartWorkout,
  onSwapDay,
  workoutName,
  date,
  subtitle,
  canSwap,
  isSwapped
}: WorkoutActionModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal - positioned at bottom on mobile, centered on desktop */}
      <div className="flex min-h-full items-end sm:items-center justify-center p-4 sm:p-0">
        <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md transform transition-all">
          {/* Handle bar for mobile */}
          <div className="sm:hidden flex justify-center pt-3">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="p-4 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  {workoutName}
                  {isSwapped && (
                    <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full flex items-center">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Swapped
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600 flex items-center mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  {format(date, 'EEEE, MMMM d, yyyy')}
                </p>
                {subtitle && (
                  <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors -mt-1 -mr-1"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 pb-6 sm:px-6 space-y-3">
            {/* Start Workout Button */}
            <button
              onClick={onStartWorkout}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <PlayCircle className="h-5 w-5" />
              Start Workout
            </button>

            {/* Swap Day Button - only for workout plans */}
            {canSwap && (
              <button
                onClick={onSwapDay}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <RefreshCw className="h-5 w-5" />
                Swap to Different Day
              </button>
            )}

            {/* Cancel Button */}
            <button
              onClick={onClose}
              className="w-full px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
